'use client'

import { RadioSelect } from '@src/components/ProductConfigurator/modules/RadioSelect/RadioSelect'
import { usePDP } from '@src/hooks/usePDP'
import { useLocale } from '@src/i18n/LocaleProvider'
import { formatCartAmount } from '@src/utils/cart.util'
import { getVariantStrikedPrice, getVariantsByFacets } from '@src/utils/product.util'

/**
 * This component provides a FacetRenderer layout for the RadioSelect component
 */
export const RadioSelectLayout: FacetLayout = ({ facetMeta, facetOptions, setFacetOption }) => {
  const { t, locale } = useLocale('common')
  const { t: tp } = useLocale('product-page')
  const { viewModel } = usePDP()
  const options = facetOptions as unknown as Array<IFacetOption<SubscriptionFrequencyFacet>>
  if (!options?.length || !viewModel) return null
  return (
    <div key={facetMeta.key}>
      <p className="pb-2 pt-4 font-medium text-bodi-gray-medium">{tp(facetMeta.title)}</p>
      {options.map((option, idx) => {
        const facetSlug = option.slug
        const facetName = (option as any)?.[
          Object.keys(option).find((key) => key.includes('DisplayName')) ?? ''
        ]?.[locale.code]

        const [optionVariant] = getVariantsByFacets({
          viewModel,
          facets: { ...viewModel.selectedVariant.facets, subscriptionFrequency: option },
        })

        const price = optionVariant?.finalPrice
        const originalPrice = optionVariant?.originalPrice
        const strikedPrice = getVariantStrikedPrice({
          viewModel,
          variant: optionVariant,
          relativeToFacet: 'subscriptionFrequency',
        })

        const isSubscriptionDiscounted = price < originalPrice && option.cadence !== 'one-time'
        const optionPriceSuffix = isSubscriptionDiscounted ? '^' : ''

        return (
          <RadioSelect
            facetKey={facetMeta.key}
            index={idx}
            key={option.slug}
            label={option.marketingName?.[locale.code]}
            onChange={() => {
              const facetOption = options.find((o) => o.slug === facetSlug)
              if (facetOption) setFacetOption(facetMeta.key, facetOption)
            }}
            value={facetSlug}
            name={facetName}
            finalPrice={formatCartAmount(price, t) + optionPriceSuffix}
            strikedPrice={strikedPrice ? formatCartAmount(strikedPrice, t) : undefined}
            header={option.bannerText?.[locale.code]}
            description={option.description?.[locale.code]}
            disclaimer={
              isSubscriptionDiscounted
                ? tp('subscription-discount-disclaimer', {
                    amount: formatCartAmount(optionVariant.originalPrice, t),
                  })
                : undefined
            }
            id={facetSlug}
            checked={option.isSelected}
            className="mb-2"
            isAvailable={option.isAvailable}
            isSelected={option.isSelected}
          />
        )
      })}
    </div>
  )
}
